import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function MarketOrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const hasCaptured = useRef(false);
  const orderId = searchParams.get("token") || "";

  useEffect(() => {
    if (loading) return;
    if (hasCaptured.current) return;
    hasCaptured.current = true;
    if (!orderId) {
      setStatus("error");
      setError("Référence de commande manquante.");
      return;
    }
    if (!user) {
      setStatus("error");
      setError("Session expirée — reconnectez-vous pour confirmer votre commande.");
      return;
    }
    const capture = async () => {
      try {
        const res = await api.post(`/market/orders/capture/${orderId}`);
        setStatus(res.data.payment_status === "paid" ? "paid" : "pending");
      } catch (err) {
        setStatus("error");
        setError(formatApiError(err));
      }
    };
    capture();
  }, [orderId, user, loading]);

  return (
    <div data-testid="market-order-success-page" className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 text-brand-turquoise animate-spin mx-auto mb-4" />
            <p className="text-brand-navy font-medium">Confirmation du paiement en cours…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-brand-turquoise mx-auto mb-4" />
            <h1 data-testid="market-order-success-title" className="text-xl font-bold text-brand-navy mb-2">
              Commande confirmée
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              Votre paiement a été reçu. Le vendeur a été notifié de votre commande.
            </p>
            <button
              data-testid="market-order-success-purchases"
              onClick={() => navigate("/marche?tab=achats")}
              className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
            >
              Voir mes achats
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-10 h-10 text-brand-orange animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-brand-navy mb-2">Paiement en cours de traitement</h1>
            <p className="text-sm text-slate-500 mb-6">PayPal finalise la transaction. Votre commande apparaîtra sous peu.</p>
            <button
              data-testid="market-order-pending-purchases"
              onClick={() => navigate("/marche?tab=achats")}
              className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
            >
              Voir mes achats
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <h1 data-testid="market-order-error-title" className="text-xl font-bold text-red-600 mb-2">Erreur</h1>
            <p data-testid="market-order-error-message" className="text-sm text-slate-500 mb-6">{error}</p>
            <button
              data-testid="market-order-error-market"
              onClick={() => navigate("/marche?tab=achats")}
              className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
            >
              Retour au marché
            </button>
          </>
        )}
      </div>
    </div>
  );
}
